import { useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce.ts';
import {
  APP_MENU_ITEMS,
  type AppMenuItem,
} from '@/features/dashboard/_root/appMenu.ts';
import useGlobalSearch, {
  isSearchableTerm,
} from '@/features/dashboard/globalSearch/hooks/useGlobalSearch.ts';
import { hasAnyHit } from '@/features/dashboard/globalSearch/globalSearchGroups.ts';
import type { GlobalSearchResultsDto } from '@/features/dashboard/globalSearch/types/GlobalSearchResultsDto.ts';

export interface GlobalSearchState {
  term: string;
  setTerm: (term: string) => void;
  /** „Szukaj też w anulowanych” — `reset()` go nie rusza, bo przełącznik zostaje widoczny. */
  includeCancelled: boolean;
  setIncludeCancelled: (includeCancelled: boolean) => void;
  /** Sekcje aplikacji pasujące etykietą — przy pustej frazie wszystkie. */
  sections: readonly AppMenuItem[];
  /** Rekordy z serwisu; `undefined` dopóki fraza jest za krótka albo trwa pobieranie. */
  results: GlobalSearchResultsDto | undefined;
  waiting: boolean;
  noResults: boolean;
  reset: () => void;
}

const normalize = (value: string) => value.trim().toLocaleLowerCase('pl');

/**
 * Jeden stan dla obu wyszukiwarek — okna na szerokim ekranie i pełnego ekranu
 * na telefonie. Sekcje filtrowane są lokalnie po etykiecie, rekordy przez
 * serwis, z opóźnieniem 300 ms od ostatniej litery.
 */
const useGlobalSearchState = (): GlobalSearchState => {
  const [term, setTerm] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const debouncedTerm = useDebounce(term, 300);
  const { data, isFetching } = useGlobalSearch(debouncedTerm, includeCancelled);

  const searching = isSearchableTerm(term);
  const waiting = searching && (isFetching || !data);
  const results = searching && !waiting ? data : undefined;

  const sections = APP_MENU_ITEMS.filter((item) =>
    normalize(item.label).includes(normalize(term)),
  );

  const noResults =
    results !== undefined && sections.length === 0 && !hasAnyHit(results);

  return {
    term,
    setTerm,
    includeCancelled,
    setIncludeCancelled,
    sections,
    results,
    waiting,
    noResults,
    reset: () => setTerm(''),
  };
};

export default useGlobalSearchState;
